import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Lock, Mail, Eye, EyeOff, ArrowRight, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SecureSphereLogin = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [searchParams] = [new URLSearchParams(window.location.search)];
  const redirectTo = searchParams.get('redirect') || '/dashboard';
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isLogin) {
        await login(formData.email, formData.password);
        navigate(redirectTo);
      } else {
        await register(formData.email, formData.password, formData.name);
        navigate(redirectTo);
      }
    } catch (err) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    "URL Scanner & Threat Detection",
    "SMS Phishing Analysis",
    "IoT Device Security",
    "Automotive V2X Protection",
    "GSM Fraud Detection",
    "AI Security Agents"
  ];

  return (
    <div className="min-h-screen bg-[#1a1a1a] flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F7B731]/20 via-[#F26419]/10 to-transparent" />
        <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/97077/pexels-photo-97077.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')] bg-cover bg-center opacity-5" />
        
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div>
            <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
              <img src="/infuse-logo.jpg" alt="Infuse.AI" className="h-12 w-12 rounded-xl" />
              <div>
                <span className="text-2xl font-bold">
                  <span className="text-white">Infuse</span>
                  <span className="text-[#F26419]">.AI</span>
                  <span className="text-neutral-400 text-sm align-top">™</span>
                </span>
              </div>
            </div>
          </div>

          <div className="space-y-8">
            <div>
              <h1 className="text-4xl font-bold text-white mb-4">
                <span className="bg-gradient-to-r from-[#F7B731] to-[#F26419] bg-clip-text text-transparent">SecureSphere</span>
                <span className="block text-white text-2xl mt-2">Telco-Grade Enterprise Security</span>
              </h1>
              <p className="text-neutral-400 text-lg">
                Protect your infrastructure with carrier-grade security solutions trusted by enterprises worldwide.
              </p>
            </div>

            <div className="space-y-4">
              {features.map((feature, index) => (
                <motion.div
                  key={feature}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="w-6 h-6 rounded-full bg-gradient-to-r from-[#F7B731] to-[#F26419] flex items-center justify-center">
                    <Check className="w-3 h-3 text-black" />
                  </div>
                  <span className="text-neutral-300">{feature}</span>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="text-sm text-neutral-500">
            &copy; 2026 Infuse Technologies. All rights reserved.
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <img src="/infuse-logo.jpg" alt="Infuse-Ai" className="h-10 w-10 rounded-lg" />
            <span className="text-xl font-bold bg-gradient-to-r from-[#F7B731] to-[#F26419] bg-clip-text text-transparent">
              SecureSphere
            </span>
          </div>

          <div className="bg-[#242424] rounded-2xl p-8 border border-white/10">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-[#F7B731] to-[#F26419] flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-black" />
              </div>
              <h2 className="text-2xl font-bold text-white">
                {isLogin ? 'Welcome Back' : 'Create Account'}
              </h2>
              <p className="text-neutral-500 mt-2">
                {isLogin ? 'Sign in to access your security dashboard' : 'Start protecting your infrastructure'}
              </p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl text-red-400 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {!isLogin && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-neutral-400 mb-2">Full Name</label>
                    <input
                      type="text"
                      data-testid="input-name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-xl text-white placeholder-neutral-600 focus:outline-none focus:border-orange-500/50 transition"
                      placeholder="John Doe"
                      required={!isLogin}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-400 mb-2">Company</label>
                    <input
                      type="text"
                      data-testid="input-company"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full px-4 py-3 bg-black/50 border border-white/10 rounded-xl text-white placeholder-neutral-600 focus:outline-none focus:border-orange-500/50 transition"
                      placeholder="Acme Corp"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-neutral-400 mb-2">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-600" />
                  <input
                    type="email"
                    data-testid="input-email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full pl-12 pr-4 py-3 bg-black/50 border border-white/10 rounded-xl text-white placeholder-neutral-600 focus:outline-none focus:border-orange-500/50 transition"
                    placeholder="you@company.com"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-neutral-400 mb-2">Password</label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-600" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    data-testid="input-password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full pl-12 pr-12 py-3 bg-black/50 border border-white/10 rounded-xl text-white placeholder-neutral-600 focus:outline-none focus:border-orange-500/50 transition"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-600 hover:text-neutral-400 transition"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {isLogin && (
                <div className="flex items-center justify-between text-sm">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" className="w-4 h-4 rounded border-white/20 bg-black/50 text-orange-500 focus:ring-orange-500" />
                    <span className="text-neutral-400">Remember me</span>
                  </label>
                  <button type="button" onClick={() => navigate('/forgot-password')} className="text-orange-400 hover:text-orange-300 transition">Forgot password?</button>
                </div>
              )}

              <button
                type="submit"
                data-testid="submit-btn"
                disabled={loading}
                className="w-full py-4 bg-gradient-to-r from-[#F7B731] to-[#F26419] text-black font-semibold rounded-xl hover:shadow-[0_0_30px_rgba(242,100,25,0.4)] transition-all transform hover:-translate-y-0.5 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                ) : (
                  <>
                    {isLogin ? 'Sign In' : 'Create Account'}
                    <ArrowRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-neutral-500">
                {isLogin ? "Don't have an account?" : "Already have an account?"}
                <button
                  onClick={() => setIsLogin(!isLogin)}
                  className="ml-2 text-orange-400 hover:text-orange-300 transition font-medium"
                >
                  {isLogin ? 'Sign up' : 'Sign in'}
                </button>
              </p>
            </div>
          </div>

          <p className="text-center text-xs text-neutral-600 mt-6">
            By signing in, you agree to our <a href="#" className="text-orange-400">Terms of Service</a> and <a href="#" className="text-orange-400">Privacy Policy</a>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default SecureSphereLogin;
